import React, { lazy, Suspense, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Portfolio from './sections/Portfolio';
import About from './sections/About';
import Press from './sections/Press';
import Publications from './sections/Publications';
import Contact from './sections/Contact';
import Seo from './seo/Seo';

// Lazily loaded routes keep the initial bundle lean
const VoxelEarth = lazy(() => import('pages/projects/VoxelEarth'));
const FireAndSmokeDigitalTwin = lazy(
  () => import('pages/projects/FireAndSmokeDigitalTwin')
);
const CrowdsourcedStreetview = lazy(
  () => import('pages/projects/CrowdsourcedStreetview')
);
const Treefolio = lazy(() => import('pages/projects/Treefolio'));
const ComputeGPT = lazy(() => import('pages/projects/ComputeGPT'));
const AustinDigitalTwin = lazy(() => import('pages/projects/AustinDigitalTwin'));
const Recyclar = lazy(() => import('pages/projects/Recyclar'));
const UTAustin3DVirtualTour = lazy(
  () => import('pages/projects/UTAustin3DVirtualTour')
);
const HeatRiskDashboard = lazy(
  () => import('pages/projects/HeatRiskDashboard')
);
const AiFeed = lazy(() => import('pages/projects/AIFeed'));
const WixScraper = lazy(() => import('pages/tools/WixScraper'));
const CentralDocumentation = lazy(
  () => import('pages/projects/CentralDocumentation')
);
const ACRC = lazy(() => import('pages/tools/ACRC'));
const PrincessAndProtector = lazy(
  () => import('pages/games/PrincessAndProtector')
);
const SocialPuzzle = lazy(() => import('pages/games/SocialPuzzle'));
const TimeTrodden = lazy(() => import('pages/games/TimeTrodden'));
const GoodKnight = lazy(() => import('pages/games/GoodKnight'));
const HallowedIsland = lazy(() => import('pages/games/HallowedIsland'));
const UpdateLauncher = lazy(() => import('pages/tools/UpdateLauncher'));
const GPTAutoScraper = lazy(() => import('pages/tools/GPTAutoScraper'));
const AnotherOne = lazy(() => import('pages/games/AnotherOne'));
const MonsteraLoginMenu = lazy(
  () => import('pages/tools/MonsteraLoginMenu')
);
const DuckDuckGoImageScraper = lazy(
  () => import('pages/tools/DuckDuckGoImageScraper')
);
const BingAIUnofficialAPI = lazy(
  () => import('pages/tools/BingAIUnofficialAPI')
);
const StableDiffusionInUnity = lazy(
  () => import('pages/tutorials/StableDiffusionInUnityTutorial')
);
const Physics3DInUnity = lazy(
  () => import('pages/tutorials/Physics3DInUnity')
);
const RealmOfTheMadFrogTutorial = lazy(
  () => import('pages/tutorials/RealmOfTheMadFrogTutorial')
);
const GenerativeDialogueTutorial = lazy(
  () => import('pages/tutorials/GenerativeDialogueTutorial')
);
const VolitionTutorial = lazy(() => import('pages/tutorials/VolitionTutorial'));
const CityContributor = lazy(() => import('pages/projects/CityContributor'));
const RelayServer = lazy(() => import('pages/tools/RelayServer'));
const UrbanTessellation = lazy(
  () => import('pages/projects/UrbanTessellation')
);
const Gitchat = lazy(() => import('pages/projects/GitChat'));
const IndoorTemperature = lazy(
  () => import('pages/projects/IndoorTemperature')
);
const DataJourney = lazy(() => import('pages/projects/DataJourney'));
const LLMCompression = lazy(() => import('pages/projects/LLMCompression'));
const TransferLearning = lazy(
  () => import('pages/projects/TransferLearning')
);
const MeshTorrent = lazy(() => import('pages/projects/MeshTorrent'));
const WebSocketMultiplayerTutorial = lazy(
  () => import('pages/tutorials/WebSocketMultiplayerTutorial')
);
const DoorInteractionTutorial = lazy(
  () => import('pages/tutorials/DoorInteractionTutorial')
);
const ServerlessPortfolio = lazy(
  () => import('pages/tools/ServerlessPortfolio')
);
const RigTorrent = lazy(() => import('pages/projects/RigTorrent'));
const EthicalGames = lazy(() => import('pages/projects/EthicalGames'));
const Login = lazy(() => import('pages/Login'));
const TSXEditor = lazy(() => import('pages/Edit'));

const RouteFallback = () => (
  <div className="py-16 text-center text-gray-500">Loading experience...</div>
);

const ScrollToTop = () => {
  const location = useLocation();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.pathname]);

  return null;
};

const App: React.FC = () => {
  const [theme, setTheme] = useState<'light' | 'dark'>(
    () => localStorage.getItem('theme') as 'light' | 'dark' || 'light'
  );

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
  };

  useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [theme]);

  return (
    <Router>
      <ScrollToTop />
      <Seo theme={theme} />
      <div className="min-h-screen flex flex-col bg-slate-50 dark:bg-gray-900 transition-colors">
        <Navbar theme={theme} toggleTheme={toggleTheme} />
        <main className="flex-grow w-full">
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<Portfolio />} />
              <Route path="/about" element={<About />} />
              <Route path="/press" element={<Press />} />
              <Route path="/publications" element={<Publications />} />
              <Route path="/contact" element={<Contact />} />

              {/* Admin Login */}
              <Route path="/login" element={<Login />} />
              <Route path="/edit" element={<TSXEditor />} />

              {/* Projects */}
              <Route
                path="/projects/voxel-earth"
                element={<VoxelEarth theme={theme} toggleTheme={toggleTheme} />}
              />
              <Route
                path="/projects/fire-smoke-digital-twin"
                element={<FireAndSmokeDigitalTwin theme={theme} toggleTheme={toggleTheme} />}
              />
              <Route
                path="/projects/crowdsourced-streetview"
                element={<CrowdsourcedStreetview theme={theme} toggleTheme={toggleTheme} />}
              />
              <Route
                path="/projects/treefolio"
                element={<Treefolio theme={theme} toggleTheme={toggleTheme} />}
              />
              <Route
                path="/projects/computegpt"
                element={<ComputeGPT theme={theme} toggleTheme={toggleTheme} />}
              />
              <Route
                path="/projects/austin-digital-twin"
                element={<AustinDigitalTwin theme={theme} toggleTheme={toggleTheme} />}
              />
              <Route
                path="/projects/recyclar"
                element={<Recyclar theme={theme} toggleTheme={toggleTheme} />}
              />
              <Route
                path="/projects/ut-austin-3d-virtual-tour"
                element={<UTAustin3DVirtualTour theme={theme} toggleTheme={toggleTheme} />}
              />
              <Route
                path="/projects/heat-risk-dashboard"
                element={<HeatRiskDashboard theme={theme} toggleTheme={toggleTheme} />}
              />
              <Route
                path="/projects/ai-feed"
                element={<AiFeed theme={theme} toggleTheme={toggleTheme} />}
              />
              <Route
                path="/tools/wix-scraper"
                element={<WixScraper theme={theme} toggleTheme={toggleTheme} />}
              />
              <Route
                path="/projects/central-documentation"
                element={<CentralDocumentation theme={theme} toggleTheme={toggleTheme} />}
              />
              <Route
                path="/tools/acrc"
                element={<ACRC theme={theme} toggleTheme={toggleTheme} />}
              />
              <Route
                path="/games/princess-and-protector"
                element={<PrincessAndProtector theme={theme} toggleTheme={toggleTheme} />}
              />
              <Route
                path="/games/social-puzzle"
                element={<SocialPuzzle theme={theme} toggleTheme={toggleTheme} />}
              />
              <Route
                path="/games/time-trodden"
                element={<TimeTrodden theme={theme} toggleTheme={toggleTheme} />}
              />
              <Route
                path="/games/good-knight"
                element={<GoodKnight theme={theme} toggleTheme={toggleTheme} />}
              />
              <Route
                path="/games/hallowed-island"
                element={<HallowedIsland theme={theme} toggleTheme={toggleTheme} />}
              />
              <Route
                path="/tools/update-launcher"
                element={<UpdateLauncher theme={theme} toggleTheme={toggleTheme} />}
              />
              <Route
                path="/tools/gpt-auto-scraper"
                element={<GPTAutoScraper theme={theme} toggleTheme={toggleTheme} />}
              />
              <Route
                path="/games/another-one"
                element={<AnotherOne theme={theme} toggleTheme={toggleTheme} />}
              />
              <Route
                path="/tools/monstera-login-menu"
                element={<MonsteraLoginMenu theme={theme} toggleTheme={toggleTheme} />}
              />
              <Route
                path="/tools/duckduckgo-image-scraper"
                element={<DuckDuckGoImageScraper theme={theme} toggleTheme={toggleTheme} />}
              />
              <Route
                path="/tools/bingai-unofficial-api"
                element={<BingAIUnofficialAPI theme={theme} toggleTheme={toggleTheme} />}
              />
              <Route
                path="/tutorials/stable-diffusion-in-unity"
                element={<StableDiffusionInUnity theme={theme} toggleTheme={toggleTheme} />}
              />
              <Route
                path="/tutorials/physics-3d-in-unity"
                element={<Physics3DInUnity theme={theme} toggleTheme={toggleTheme} />}
              />
              <Route
                path="/tutorials/realm-of-the-mad-frog"
                element={<RealmOfTheMadFrogTutorial theme={theme} toggleTheme={toggleTheme} />}
              />
              <Route
                path="/tutorials/generative-dialogue"
                element={<GenerativeDialogueTutorial theme={theme} toggleTheme={toggleTheme} />}
              />
              <Route
                path="/tutorials/volition"
                element={<VolitionTutorial theme={theme} toggleTheme={toggleTheme} />}
              />
              <Route
                path="/projects/city-contributor"
                element={<CityContributor theme={theme} toggleTheme={toggleTheme} />}
              />
              <Route
                path="/tools/relay-server"
                element={<RelayServer theme={theme} toggleTheme={toggleTheme} />}
              />
              <Route
                path="/projects/urban-tessellation"
                element={<UrbanTessellation theme={theme} toggleTheme={toggleTheme} />}
              />
              <Route
                path="/projects/gitchat"
                element={<Gitchat theme={theme} toggleTheme={toggleTheme} />}
              />
              <Route
                path="/projects/indoor-temperature"
                element={<IndoorTemperature theme={theme} toggleTheme={toggleTheme} />}
              />
              <Route
                path="/projects/datajourney"
                element={<DataJourney theme={theme} toggleTheme={toggleTheme} />}
              />
              <Route
                path="/projects/llm-compression"
                element={<LLMCompression theme={theme} toggleTheme={toggleTheme} />}
              />
              <Route
                path="/projects/transfer-learning"
                element={<TransferLearning theme={theme} toggleTheme={toggleTheme} />}
              />
              <Route
                path="/projects/meshtorrent"
                element={<MeshTorrent theme={theme} toggleTheme={toggleTheme} />}
              />
              <Route
                path="/tutorials/websocket-multiplayer"
                element={<WebSocketMultiplayerTutorial theme={theme} toggleTheme={toggleTheme} />}
              />
              <Route
                path="/tutorials/door-interaction"
                element={<DoorInteractionTutorial theme={theme} toggleTheme={toggleTheme} />}
              />
              <Route
                path="/tools/serverless-portfolio"
                element={<ServerlessPortfolio theme={theme} toggleTheme={toggleTheme} />}
              />
              <Route
                path="/projects/rigtorrent"
                element={<RigTorrent theme={theme} toggleTheme={toggleTheme} />}
              />
              <Route
                path="/projects/ethical-games"
                element={<EthicalGames theme={theme} toggleTheme={toggleTheme} />}
              />
            </Routes>
          </Suspense>
        </main>
        <Footer />
      </div>
    </Router>
  );
};

export default App;
